export type ChatLog = {
  id: number;
  role: string;
  message: string;
  timestamp: string;
  chat_session: number | null;
}

export interface NavLog {
  id?: number;
  url: string;
  title: string;
  image_url: string;
  session_id: number;
  network_logs: any[];
}

export interface NetworkLog extends Record<string, any> {
  url: string;
  method: string;
  headers?: Record<string, string>;
  request?: {
    url: string;
    method: string;
    headers?: Record<string, string>;
    postData?: string;
    postDataEntries?: { bytes: string }[];
    isSameSite?: boolean;
    hasPostData?: boolean;
    referrerPolicy?: string;
    initialPriority?: string;
    mixedContentType?: string;
  };
  postData?: string;
  response?: any;
  responseBody?: string;
  timestamp?: number;
  postData_encoding?: string;
}

export interface Payload {
  id: number;
  label: string;
  description?: string;
  original_network_log: NetworkLog;
  updated_network_log: NetworkLog;
  extracted_url?: string;
  extracted_title?: string;
  saved_at: string;
}

export type SessionHistory = {
  id: number;
  start_time: string;
  end_time: string | null;
  first_message: string | null;
  is_browser: boolean;
  target: string | null;
  tool: ToolType | null;
}

export type IsBrowserStepsLoading = {
  message_id: number;
  isLoading: boolean;
}

export enum RequestStatuses {
  IDLE = 'idle',
  PENDING = 'pending',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed'
}

export enum ToolType {
  OSINT = 'osint',
  CODER = 'coder',
  BLANK = 'blank',
  BROWSER = 'browser',
  FACESEARCH = 'facesearch',
  CTF = 'ctf',
}

type AuthState = {
  currentUser: string;
  token: string;
  userPicture: string;
}

type ChatState = {
  chatLog: ChatLog[];
  navLog: NavLog[];
  savedPayloads: Payload[];
  fetchSavedPayloadsStatus: RequestStatuses;
  apiMap: ApiMap | null;
  fetchApiMapStatus: RequestStatuses;
  currentSessionId: number;
  sessionHistory: SessionHistory[];
  sessionsWithBlockedUserPrompt: number[];
  isWebSocketOpen: boolean;
  fetchChatSessionHistoryStatus: RequestStatuses;
  sessionsWithSelectedTool: Record<number, ToolType>;
  isBrowserVisible: boolean;
  isBrowserStepsLoading: IsBrowserStepsLoading[];
  toolStatus: string;
}

type SubscriptionState = {
  isSubscribed: boolean;
  userMessageCount: number;
  numberOfMessagesBeforeCharge: number;
  isPaymentCardVisible: boolean;
  specialLimitApplies: boolean;
}

export type StateSchema = {
  auth: AuthState;
  chat: ChatState;
  subscription: SubscriptionState;
}

export type ThunkConfig = {
  state: StateSchema;
}

export type CheckAuthHandler = () => void;

export type SendMessageHandler = (message: string) => void;

export type StatusChangeHandler = (status: boolean) => void;

export type ApiEndpointDoc = {
  method: string;
  path: string;
  description: string;
  parameters: {
    query: Record<string, string>;
    body: Record<string, string>;
    headers: Record<string, string>;
  };
  responses: Record<string, string>;
  authentication: string;
  potential_vulnerabilities: string[];
};

export type ApiFinding = {
  finding: string;
  risk_level: string;
  description: string;
  exploitation_potential?: string;
  affected_requests?: string[];
};

export type ApiMapPerSite = {
  api_documentation: ApiEndpointDoc[];
  security_findings: ApiFinding[];
};

export type ApiMap = Record<string, ApiMapPerSite>; 